import { useState, useEffect, useRef } from "react";
import { Draggable } from "@hello-pangea/dnd";
import { Zap, CalendarDays, GripVertical, UserPlus } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { TASK_STATUS_ICON } from "./constants";

export const TaskCard = ({ task, index, onUpdate, teamMembers }) => {
  const icon = TASK_STATUS_ICON[task.status] || TASK_STATUS_ICON.todo;
  const [showAssignee, setShowAssignee] = useState(false);
  const [showDate, setShowDate] = useState(false);
  const assigneeRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (assigneeRef.current && !assigneeRef.current.contains(e.target)) setShowAssignee(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef} {...provided.draggableProps}
          className={`bg-white border rounded-xl px-3.5 py-3 mb-2 transition-shadow group ${snapshot.isDragging ? "shadow-lg border-[#014d7d]/30 ring-1 ring-[#014d7d]/10" : "border-border/50 hover:shadow-sm"}`}
          data-testid={`task-card-${task.id}`}
        >
          <div className="flex items-start gap-2">
            <div {...provided.dragHandleProps} className="mt-1 opacity-0 group-hover:opacity-40 cursor-grab">
              <GripVertical className="w-3 h-3" />
            </div>
            <div className="mt-0.5">{icon}</div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-foreground leading-snug truncate">{task.title}</p>
              <div className="flex items-center gap-2 mt-1.5 text-[10px] text-foreground/50 flex-wrap">
                {task.stage && <span className="px-1.5 py-0.5 bg-foreground/[0.04] rounded capitalize">{task.stage}</span>}
                {task.automation_type === "automated" && <span className="text-amber-500 flex items-center gap-0.5"><Zap className="w-2.5 h-2.5" />Auto</span>}
                <Popover open={showDate} onOpenChange={setShowDate}>
                  <PopoverTrigger asChild>
                    <button className={`flex items-center gap-0.5 px-1 py-0.5 rounded hover:bg-foreground/5 ${task.due_date ? "" : "opacity-0 group-hover:opacity-100"}`} data-testid={`task-due-${task.id}`}>
                      <CalendarDays className="w-2.5 h-2.5" />
                      <span>{task.due_date ? new Date(task.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "Set date"}</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[100]" align="start">
                    <Calendar mode="single" selected={task.due_date ? new Date(task.due_date) : undefined}
                      onSelect={(date) => { if (date) onUpdate(task.id, { due_date: date.toISOString().slice(0, 10) }); setShowDate(false); }} />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div className="relative shrink-0" ref={assigneeRef}>
              <button onClick={() => setShowAssignee(p => !p)} className="group/avatar" data-testid={`task-assignee-${task.id}`} title={task.assignee?.name || "Assign"}>
                {task.assignee ? (
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[7px] font-bold mt-0.5" style={{ background: task.assignee.color || "#999" }}>{task.assignee.initials}</div>
                ) : (
                  <div className="w-5 h-5 rounded-full border border-dashed border-foreground/20 flex items-center justify-center mt-0.5 opacity-0 group-hover:opacity-100">
                    <UserPlus className="w-2.5 h-2.5 text-foreground/45" />
                  </div>
                )}
              </button>
              {showAssignee && teamMembers?.length > 0 && (
                <div className="absolute right-0 top-full mt-1 w-40 bg-white rounded-xl shadow-elevated border border-border/60 overflow-hidden z-50" style={{ animation: "cardFadeIn 0.1s ease-out" }} data-testid={`assignee-picker-${task.id}`}>
                  <div className="px-3 py-2 border-b border-border/30"><p className="text-[9px] font-bold text-foreground/45 uppercase tracking-wider">Assign to</p></div>
                  {teamMembers?.map((m, i) => (
                    <button key={i} onClick={() => { onUpdate(task.id, { assignee: { name: m.name, initials: m.initials, color: m.color } }); setShowAssignee(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-foreground/[0.03] text-left" data-testid={`assign-member-${m.name}`}>
                      <div className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[6px] font-bold" style={{ background: m.color }}>{m.initials}</div>
                      <span className="text-[11px] font-medium text-foreground/60">{m.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
};
